import { type Ref, ref } from 'vue';
import { readCookie } from '~/modules/cookies-services';
import { requirejs } from '~/modules/require';
import type { NLS_Record } from '~/typings/nls/index.d.ts';

type NlsRecordKeys = keyof NLS_Record;

export function useTranslations<T extends NlsRecordKeys, U extends NLS_Record[T]>(
  filename: T,
  path = 'i18n!DS/MP3DPMakeVue3/assets/nls/',
) {
  const i18n = ref({}) as Ref<Partial<U>>;
  const language = readCookie('swymlang') || 'en';
  const promise = new Promise(async (resolve) => {
    try {
      const modules = import.meta.glob('/assets/nls/**/*.json');

      // In case of not having the files / translations, use required to check them at runtime instead
      if (!modules[`/assets/nls/${filename.toString()}_${language}.json`]) {
        const [result] = await requirejs<[any]>([`${path}${filename.toString()}`]);
        i18n.value = Object.assign(i18n.value, result);
        resolve(i18n.value);
        return;
      }

      const result = (
        (await modules[`/assets/nls/${filename.toString()}_${language}.json`]()) as any
      ).default;
      i18n.value = Object.assign(i18n.value, result);
    } catch (error) {
      console.info(`Error loading nls ${filename.toString()}`);
    }

    resolve(i18n.value);
  });

  function $i18n(key: undefined): '';
  function $i18n<V extends keyof U>(
    key: V,
    variables?: Record<string, unknown>,
    fallback?: boolean,
  ): U[V] | typeof key;
  function $i18n<V extends keyof U>(
    key: V | undefined,
    variables: undefined | Record<string, unknown> = {},
    fallback?: boolean,
  ): U[V] | '' | typeof key {
    if (!key) {
      return '';
    }

    let value = i18n.value[key] as string | undefined;
    Object.entries(variables).forEach(([key, val]) => {
      value = value?.replace(`{${key}}`, val as string);
    });

    if (!value && fallback) {
      return key;
    }

    return (value || (import.meta.env.DEV ? `__${key as string}__` : key)) as U[V] | typeof key;
  }

  return { i18n, $i18n, promise };
}
